import gql from "graphql-tag";
import * as Cookies from "es-cookie";

import client from "../apolloClient";

/**
 * Notifications fetched once per session instead of once per page.
 *
 * The bell lives in `components/navbars/Notification.jsx`, which is mounted by
 * 19 different navbars — Navbars, HomeNavbar, ScrollTopNavbar, every analysis
 * navbar, the exam instruction screens — so it is on screen for effectively the
 * whole app. It ran its own `graphql()` query with `fetchPolicy:
 * "cache-and-network"`, and because react-router unmounts and remounts the
 * navbar on each route change, that meant a fresh getStudentNotificatons
 * request on every single navigation. The result is only ever used to render an
 * unread count on a bell icon.
 *
 * This module holds that list in one place and fetches it once. It is primed at
 * login (see `primeNotifications` callers) and read by the bell from then on, so
 * moving between pages costs nothing.
 *
 * A hard refresh clears module state, so the first bell to mount after a reload
 * primes it again — one request per page load rather than per navigation. That
 * is deliberate: the alternative, persisting the list, would show a stale unread
 * count for the whole session.
 */

export const FETCH_STUDENTNOTIFICATIONS = gql`
  query($student_user_exam_id: Int!) {
    getStudentNotificatons(student_user_exam_id: $student_user_exam_id) {
      id
      title
      description
      timestamp
      image
      seen_status
    }
  }
`;

let notifications = [];
let inFlight = null;
/**
 * The student the current or last request was issued for. Set when the request
 * starts, not when it lands, so the several navbars that mount together share
 * it instead of each starting their own.
 *
 * It also keys the cache to a student: logout navigates with `history.push`,
 * not a reload, so module state survives it — without this the next student to
 * log in on the same tab would see the previous one's unread count.
 */
let requestedFor = null;

const listeners = new Set();

function currentStudentExamId() {
  const raw = Cookies.get("student_user_exam_id");
  const id = parseInt(raw, 10);
  return isNaN(id) ? null : id;
}

function notify() {
  listeners.forEach(fn => fn(notifications));
}

/** Returns an unsubscribe function; call it from componentWillUnmount. */
export function subscribeToNotifications(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNotifications() {
  return notifications;
}

/** The number the bell badge shows. `seen_status` is a string in the API. */
export function getUnseenCount() {
  return notifications.filter(n => n && n.seen_status == "0").length;
}

/**
 * Fetch the list if it has not been fetched for this student yet, and otherwise
 * do nothing. Safe to call from anywhere, as often as you like: concurrent
 * callers share one request via `inFlight`, so the several navbars that mount
 * together on a page still produce a single network call.
 *
 * @param {boolean} [force]  refetch even if already loaded — after marking
 *                           notifications as seen, for instance.
 * @returns {Promise<Array>} the current list; never rejects.
 */
export function primeNotifications(force) {
  const studentUserExamId = currentStudentExamId();

  // Logged out, or mid-registration before an enrollment exists. Nothing to
  // fetch, and asking would only produce an UNAUTHENTICATED bounce to login.
  if (studentUserExamId === null || !Cookies.get("studenttoken")) {
    return Promise.resolve(notifications);
  }

  if (studentUserExamId !== requestedFor) {
    // A different student, or the first call. Anything cached belongs to
    // someone else.
    notifications = [];
    inFlight = null;
  } else if (!force) {
    // Already fetched, or fetching, for this student — this is the case that
    // every page navigation after the first now takes.
    return inFlight || Promise.resolve(notifications);
  }

  requestedFor = studentUserExamId;
  inFlight = client
    .query({
      query: FETCH_STUDENTNOTIFICATIONS,
      variables: { student_user_exam_id: studentUserExamId },
      fetchPolicy: "network-only"
    })
    .then(result => {
      notifications = (result.data && result.data.getStudentNotificatons) || [];
      notify();
      return notifications;
    })
    .catch(error => {
      // Swallowed on purpose. This used to `alert("Server Error. " + message)`
      // straight out of the bell's render, which put a modal dialog on every
      // page of the app when notifications happened to be down. An unread badge
      // is not worth interrupting the user for; an UNAUTHENTICATED failure is
      // already handled by the error link in apolloClient.js.
      console.error("getStudentNotificatons failed", error);
      // Clearing the key lets the next mount retry. A failing server therefore
      // still gets one attempt per navigation — no worse than the behaviour
      // this replaced, and the success path is what we are optimising.
      inFlight = null;
      requestedFor = null;
      return notifications;
    });

  return inFlight;
}

/** Re-read from the server — for after notifications are marked as seen. */
export function refreshNotifications() {
  return primeNotifications(true);
}

/**
 * Drop the cached list. Call on logout so the bell empties immediately rather
 * than waiting for the next student's id to differ.
 */
export function resetNotifications() {
  notifications = [];
  inFlight = null;
  requestedFor = null;
  notify();
}

export default primeNotifications;
